import { ApolloProvider } from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import ErrorBoundary from '@components/Error/ErrorBoundary'
import WithSharedData from '@components/Shared/HOC/WithSharedData'
import LayoutRoot from '@components/Shared/Layout/LayoutRoot'
import Toast from '@components/Toast'
import { havelockAuthContext } from '@lib/context/HavelockAuthContext/HavelockAuthContext'
import { useApollo } from '@lib/graphql/apollo'
import useLogout from '@lib/hooks/useLogout'
import useRouterSubscriptions from '@lib/hooks/useRouterSubscriptions'
import { GraphqlApi } from '@mono/packages/graphql/apollo/constants'
import DialogProvider from '@mono/packages/ui/components/Dialog/provider/DialogProvider'
import Head from 'next/head'
import React, { Suspense, useMemo } from 'react'
import AccountProvider from './AccountProvider'
import {
  DateOptionsProvider,
  TranslationsProviderWrapper,
  UiComponentsTranslationsProvider,
} from './Providers'
import RedirectHandler from './RedirectHandler'

interface IAppMainProps {
  Component: any
  pageProps: Record<string, any>
}

const AppMain: React.FunctionComponent<IAppMainProps> = ({
  Component,
  pageProps,
}) => {
  const {
    getAccessTokenSilently: getProdactiveAccessToken,
    isAuthenticated: isProdactiveAuthenticated,
  } = useAuth0()
  const {
    getAccessTokenSilently: getHavelockAccessToken,
    isAuthenticated: isHavelockAuthenticated,
  } = useAuth0(havelockAuthContext)
  const { logoutClient } = useLogout()

  const authConfig = useMemo(
    () => ({
      getHavelockAccessToken,
      getProdactiveAccessToken,
      onLogout: (api: GraphqlApi) => {
        if (api === GraphqlApi.Prodactive) {
          logoutClient({
            logoutParams: {
              returnTo: window.location.origin,
            },
          })
        }
      },
    }),
    [getHavelockAccessToken, getProdactiveAccessToken, logoutClient]
  )

  const apolloClient = useApollo(
    GraphqlApi.Prodactive,
    authConfig,
    pageProps?.initialApolloState
  )

  useRouterSubscriptions()

  const CustomLayout = (Component as any).CustomLayout
  const isAuthenticated = isHavelockAuthenticated && isProdactiveAuthenticated

  return (
    <Suspense fallback={null}>
      <ApolloProvider client={apolloClient}>
        <ErrorBoundary>
          <AccountProvider>
            <ErrorBoundary>
              <WithSharedData isAuthenticated={isAuthenticated}>
                <RedirectHandler>
                  <Head>
                    <title>Manifutura</title>
                    <link
                      rel="icon"
                      type="image/x-icon"
                      href={'/favicon-default.ico'}
                    />
                  </Head>
                  <TranslationsProviderWrapper>
                    <DateOptionsProvider>
                      <UiComponentsTranslationsProvider>
                        <DialogProvider>
                          <LayoutRoot
                            CustomLayout={
                              CustomLayout
                                ? (props) => (
                                    <CustomLayout
                                      {...props}
                                      pageProps={pageProps}
                                      Component={Component}
                                    />
                                  )
                                : undefined
                            }
                          >
                            <Component {...pageProps} />
                          </LayoutRoot>
                        </DialogProvider>
                        <Toast />
                      </UiComponentsTranslationsProvider>
                    </DateOptionsProvider>
                  </TranslationsProviderWrapper>
                </RedirectHandler>
              </WithSharedData>
            </ErrorBoundary>
          </AccountProvider>
        </ErrorBoundary>
      </ApolloProvider>
    </Suspense>
  )
}

export default AppMain
